import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient.js';
import { useSelector, shallowEqual } from 'react-redux';
import useAlert from '../../alert/useAlert.js';
import { newPurchaseOperationId, purchaseConnectionWithCredits } from '../../utils/creditWalletRpc.js';
import { purchaseDeniedMessage, purchaseErrorMessage } from '../../utils/creditPurchaseMessages.js';
import { creditWalletOperationCompleted } from '../../creditWallet/WalletBalanceContext.js';

export const useMedicalScribeData = () => {
  const [patientList, setPatientList] = useState([]);
  const [consultationList, setConsultationList] = useState([]);
  const [teamMembersData, setTeamMembersData] = useState([]);
  const [newPatient, setNewPatient] = useState(false);
  const [consultationLoading, setConsultationLoading] = useState(false);
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);
  const hasMounted = useRef(false);
  const { setAlert } = useAlert();
  const viewType = 'MindVault';
  

  useEffect(() => {
    if (hasMounted.current) { return; }
    fetchSubscriptionInfo();
    fetchPatientData();
    fetchConsultationData();
    getTeamMembers();
    // fetchAllPatients();
    hasMounted.current = true;
  }, []);

  useEffect(() => {
    if (!hasMounted.current) { return; }
    fetchPatientData();
    getTeamMembers();
    fetchConsultationData();
  }, [currentOrg?.id]);

  const fetchAllPatients = async () => {
    let { data, error } = await supabase.from('patients').select('*').eq('teamid', '2f27e7b8-4653-4ea3-9288-1cf77be3b79d');
    if (error) {
      console.error(error);
    } else {
      const decryptionPromises = data.map(async (patient) => {
        const [
          nameResult,
          phoneNumberResult,
          firstNameResult,
          lastNameResult,
        ] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: patient.name }),
          supabase.rpc('decrypt_text', { encrypted_data: patient.phoneNumber }),
          supabase.rpc('decrypt_text', { encrypted_data: patient.firstName }),
          supabase.rpc('decrypt_text', { encrypted_data: patient.lastName }),
          supabase.rpc('decrypt_text', { encrypted_data: patient.lastName }),
        ]);
        if ( nameResult.error || phoneNumberResult.error || firstNameResult.error || lastNameResult.error) {
          return patient;
        }
        patient.name = nameResult.data;
        patient.phoneNumber = phoneNumberResult.data;
        patient.firstName = firstNameResult.data;
        patient.lastName = lastNameResult.data;
        return patient;
      });
      const decryptedPatients = await Promise.all(decryptionPromises);
      console.log('All Patients:', decryptedPatients);
  }}

  const fetchSubscriptionInfo = async () => {
    try {
      if (!userDetails || !userDetails.id) return;
      
      const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription info:', error);
        return;
      }
      if(!data) {
        setSubscriptionActiveStatus(false);
        return;
      }
      if (data) {
        setSubscriptionActiveStatus(data.is_active);
      }
    } catch (error) {
      console.error('Error in fetchSubscriptionInfo:', error);
    }
  };

  const checkCollaborator = async () => {
    if (!currentOrg || !userDetails) {
      return false;
    }
    const { data, error } = await supabase.from('teams').select('collaborators').eq('id', currentOrg.id).maybeSingle();

    if (error) {
      console.error("Error fetching team collaborators:", error);
      return false;
    }

    if (data && data.collaborators) {
      return data.collaborators.includes(userDetails.id);
    }
    return false;
  }

  const fetchPatientData = async () => {
    // if (userDetails?.mode === 'Corporates' && viewType === 'Vitals') {
    //   setPatientList([]);
    //   return;
    // }
    if (!currentOrg?.id) {
      setPatientList([]);
      return;
    }
    // Match on the team_ids array (not the single teamid column) so patients
    // attached to multiple teams appear in every team they belong to.
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .contains('team_ids', [currentOrg.id]);

    if (error) {
      console.error(error);
      setPatientList([]);
      return;
    }

    const filteredData = data ?? [];
    const decryptionPromises = filteredData.map(async (patient) => {
      const [
        nameResult,
        phoneCodeResult,
        phoneNumberResult,
        firstNameResult,
        lastNameResult,
        genderResult,
        ageResult,
        birthDateResult,
        hkidResult,
        remarksResult,
        statusResult,
        latestComplaintResult,
        insightsResult,
        emergencyContactResult,
        emergencyContactPersonResult,
        presentingConcernsResult,
        emailResult
      ] = await Promise.all([
        supabase.rpc('decrypt_text', { encrypted_data: patient.name }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.phone_code }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.phoneNumber }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.firstName }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.lastName }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.gender }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.age }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.birth_date }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.hkid }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.remarks }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.status }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.latestComplaint }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.insights }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.emergency_contact }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.emergency_contact_person }), 
        supabase.rpc('decrypt_text', { encrypted_data: patient.presenting_concerns }),
        supabase.rpc('decrypt_text', { encrypted_data: patient.email }),
      ]);

      if (!nameResult.error) patient.name = nameResult.data;
      if (!phoneCodeResult.error) patient.phone_code = phoneCodeResult.data;
      if (!phoneNumberResult.error) patient.phoneNumber = phoneNumberResult.data;
      if (!firstNameResult.error) patient.firstName = firstNameResult.data;
      if (!lastNameResult.error) patient.lastName = lastNameResult.data;
      if (!genderResult.error) patient.gender = genderResult.data;
      if (!ageResult.error) patient.age = ageResult.data;
      if (!birthDateResult.error) patient.birth_date = birthDateResult.data;
      if (!hkidResult.error) patient.hkid = hkidResult.data;
      if (!remarksResult.error) patient.remarks = remarksResult.data;
      if (!statusResult.error) patient.status = statusResult.data;
      if (!latestComplaintResult.error) patient.latestComplaint = latestComplaintResult.data;
      if (!insightsResult.error) patient.insights = insightsResult.data;
      if (!emergencyContactResult.error) patient.emergency_contact = emergencyContactResult.data;
      if (!emergencyContactPersonResult.error) patient.emergency_contact_person = emergencyContactPersonResult.data;
      if (!presentingConcernsResult.error) patient.presenting_concerns = presentingConcernsResult.data;
      if (!emailResult.error) patient.email = emailResult.data;
      return patient;
    });
    const decryptedPatients = await Promise.all(decryptionPromises); 
    setPatientList(decryptedPatients);
  };

  const fetchConsultationData = async () => {
    // if (userDetails?.mode === 'Corporates' && viewType === 'Vitals') {
    //   setConsultationList([]);
    //   setConsultationLoading(false);
    //   return;
    // }
    if (!userDetails?.id) {
      setConsultationList([]);
      return;
    }
    setConsultationLoading(true);
    const { data: scribeData, error: scribeError } = await supabase
      .from('patients')
      .select('id')
      .contains('case_owner', [userDetails.id]);

    if (scribeError) {
      console.error('Error fetching scribe IDs:', scribeError);
      setConsultationLoading(false);
      return;
    }

    const scribeIds = (scribeData ?? []).map((patient) => patient.id);
    if (scribeIds.length === 0) {
      setConsultationList([]);
      setConsultationLoading(false);
      return;
    }

    const { data: consultationData, error: consultationError } = await supabase
      .from('consultations')
      .select('*')
      .in('patient_id', scribeIds);

    if (consultationError) {
      console.error('Error fetching consultations:', consultationError);
    } else {
        setConsultationLoading(true);
        const decryptionPromises = consultationData.map(async (consultation) => {
        const [
          notesResult,
          transcriptionResult,
          chiefComplaintResult,
        ] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: consultation.notes }),
          supabase.rpc('decrypt_text', { encrypted_data: consultation.transcription }),
          supabase.rpc('decrypt_text', { encrypted_data: consultation.chiefComplaint }),
        ]);

        if (!notesResult.error) consultation.notes = notesResult.data;
        if (!transcriptionResult.error) consultation.transcription = transcriptionResult.data;
        if (!chiefComplaintResult.error) consultation.chiefComplaint = chiefComplaintResult.data;

        return consultation;
      });
      const decryptedConsultations = await Promise.all(decryptionPromises);

      setConsultationList(decryptedConsultations);
      setConsultationLoading(false);
    }
  };

  const fetchCorporateVitalsRecords = async ({ teamId, startIso, endIso }) => {
    if (!teamId) return { records: [], groupedByRecord: {} };

    const [scanRes, chatRes] = await Promise.all([
      supabase
        .from('scan_history')
        .select(`
          id, session_id, created_at, team_id, user_id,
          user_tag, gender, height, weight, birth,
          heart_rate, hr_variability, sbp, dbp, oxygen_saturation, respiration_rate, stress_index
        `)
        .eq('team_id', teamId)
        .is('user_id', null)
        .gte('created_at', startIso)
        .lte('created_at', endIso),
      supabase
        .from('chat_sessions')
        .select(`
          id, session_id, created_at, team_id, user_id,
          risk_level, risk_signals, user_details
        `)
        .eq('team_id', teamId)
        .is('user_id', null)
        .eq('is_deleted', false)
        .gte('created_at', startIso)
        .lte('created_at', endIso),
    ]);

    if (scanRes.error) throw scanRes.error;
    if (chatRes.error) throw chatRes.error;

    const scans = scanRes.data || [];
    const chats = chatRes.data || [];

    const parseJsonSafe = (value, fallback = {}) => {
      if (!value) return fallback;
      if (typeof value === 'object') return value;
      try { return JSON.parse(value); } catch { return fallback; }
    };

    const calculateAge = (birthYear) => {
      const y = Number(birthYear);
      if (!Number.isFinite(y) || y < 1900) return null;
      return new Date().getFullYear() - y;
    };

    const grouped = new Map();
    const getKey = (row, source) => (row.session_id ? `session:${row.session_id}` : `${source}:${row.id}`);

    scans.forEach((s) => {
      const key = getKey(s, 'scan');
      if (!grouped.has(key)) grouped.set(key, { key, session_id: s.session_id || null, scans: [], chats: [] });
      grouped.get(key).scans.push(s);
    });

    chats.forEach((c) => {
      const key = getKey(c, 'chat');
      if (!grouped.has(key)) grouped.set(key, { key, session_id: c.session_id || null, scans: [], chats: [] });
      grouped.get(key).chats.push(c);
    });

    const groupedByRecord = {};
    const records = [];

    Array.from(grouped.values()).forEach((g, idx) => {
      const latestScan = [...g.scans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      const latestChat = [...g.chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      const userDetailsObj = parseJsonSafe(latestChat?.user_details, {});

      const sex = userDetailsObj?.sex || latestScan?.gender || 'Unknown';
      const height = userDetailsObj?.height ?? latestScan?.height ?? null;
      const weight = userDetailsObj?.weight ?? latestScan?.weight ?? null;
      const userId = userDetailsObj?.userID || latestScan?.user_tag || null;
      const birthYear = userDetailsObj?.birthYear ?? latestScan?.birth ?? null;
      const bpStatus = userDetailsObj?.ht ?? latestScan?.blood_pressure ?? null;

      const age = calculateAge(birthYear);

      const scanTs = latestScan?.created_at ? new Date(latestScan.created_at).getTime() : null;
      const chatTs = latestChat?.created_at ? new Date(latestChat.created_at).getTime() : null;
      const createdAtTs = Math.max(scanTs || 0, chatTs || 0);

      const displayName =
        latestScan?.user_tag ||
        userDetailsObj?.name ||
        (g.session_id ? `Session ${String(g.session_id).slice(0, 8)}` : `Record ${idx + 1}`);

      const record = {
        id: g.key,
        session_id: g.session_id,
        name: displayName,
        gender: sex,
        age,
        status: 'Active',
        createdAt: createdAtTs > 0 ? new Date(createdAtTs).toISOString() : null,
        profile: {
          sex,
          height,
          weight,
          userId,
          userTag: latestScan?.user_tag || null,
          birthYear,
          bpStatus,
        },
      };

      groupedByRecord[g.key] = { scans: g.scans, chats: g.chats, latestScan, latestChat };
      records.push(record);
    });

    return { records, groupedByRecord };
  };

  const getTeamMembers = async () => {
    if(!currentOrg) {
      return;
    }
    try {
      const { data: memberData, error: memberDataError } = await supabase
        .from('team_users')
        .select('user_id, role')
        .eq('team_id', currentOrg ? currentOrg.id : '');

      if (memberDataError) {
        console.error("Error fetching team members:", memberDataError);
        return;
      }

      const roleByUserId = Object.fromEntries(memberData.map(m => [m.user_id, m.role]));
      const teamMembersIDs = memberData.map(member => member.user_id);
      const { data: usersData, error: usersError } = await supabase
        .from('user_accounts')
        .select('id, name')
        .in('id', teamMembersIDs);

      if (usersError) {
        console.error("Error fetching user accounts:", usersError);
        return;
      }

      const decryptedUsers = await Promise.all(
        usersData.map(async (userData) => {
          const { data: decryptedName, error: decryptError } = await supabase.rpc(
            'decrypt_text',
            { encrypted_data: userData.name }
          );

          if (decryptError) {
            console.error("Error decrypting name:", decryptError);
            return "Error decrypting name";
          }
          return {
            id: userData.id,
            name: decryptedName,
            role: roleByUserId[userData.id] ?? null,
          }
        })
      );
      setTeamMembersData(decryptedUsers);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  // Returns true when the patient row was created (the form then closes),
  // false when it was not (the form stays open and the alert explains why).
  // connectMindvault runs the 10-credit connection purchase AFTER the insert
  // succeeds, so a failed purchase never blocks the client record — the
  // per-patient connect surface in the patient list is the retry path.
  const createPatient = async ( firstName, lastName, phoneNumber, hkid, gender, birth_date, case_owner, remarks, preExistingConditions, emergencyContact, emergencyContactPerson, presentingConcerns, diagnosis, status, countryCode, email, connectMindvault = false ) => {
    let birthDate = null;
    let age = null;

    if (birth_date) {
      birthDate = new Date(birth_date);
      const today = new Date();
      age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
    }
    let join_date = new Date();
    const caseOwnerIds = case_owner.map(owner => owner.id);

    const { data: isUnique, error: rpcError } = await supabase.rpc('check_patient_email_unique', { p_email: email, p_team_id: currentOrg?.id ?? null });
    if (rpcError) {
      console.error('Email uniqueness check failed:', rpcError);
      setAlert('Could not validate email. Please try again.', 'error');
      return false;
    }
    if (!isUnique) {
      setAlert('A patient with this email address already exists. Each patient must have a unique email.', 'error');
      return false;
    }

    let { data: inserted, error } = await supabase.from('patients').insert([{
      name: `${lastName} ${firstName}`,
      firstName,
      lastName,
      phoneNumber,
      phone_code: countryCode,
      hkid,
      gender,
      birth_date: birthDate,
      case_owner: caseOwnerIds,
      preExistingConditions,
      remarks,
      status,
      age,
      join_date,
      teamid: currentOrg ? currentOrg.id : '',
      team_ids: currentOrg ? [currentOrg.id] : [],
      emergency_contact: emergencyContact || '',
      emergency_contact_person: emergencyContactPerson || '',
      presenting_concerns: presentingConcerns || '',
      diagnosis: diagnosis || [],
      email: email || ''
    }]).select('id');

    if (error){
      console.error(error);
      if (error.code === '42501') {
        setAlert('Your account is not a member of this organization. Please log out and log in again.', 'error');
      } else {
        setAlert('Error creating patient. Please try again.', 'error');
      }
      return false;
    }
    if (!inserted || inserted.length === 0) {
      setAlert('Patient could not be created — you may not have access to this organization.', 'error');
      return false;
    }

    if (connectMindvault && email) {
      // One operation id per purchase intent, generated client-side; the RPC
      // is idempotent per (therapist, operation id) and derives the therapist
      // from auth.uid() — never send a user id. Single attempt: the patient
      // row already exists, so a retry here would fight the email-uniqueness
      // gate; the alert below names the per-patient connect surface instead.
      const operationId = newPurchaseOperationId();
      const { data: purchaseData, error: purchaseError } = await purchaseConnectionWithCredits(email.trim(), operationId);
      if (purchaseError) {
        console.error('MindVault connection purchase failed:', purchaseError);
        setAlert(purchaseErrorMessage(purchaseError), 'error');
      } else if (!purchaseData?.purchased) {
        setAlert(purchaseDeniedMessage(purchaseData)
          || 'The credit purchase could not be completed. The client was added without a MindVault connection.', 'error');
      } else {
        creditWalletOperationCompleted();
      }
    }

    setNewPatient(false);
    fetchPatientData();
    fetchConsultationData();
    return true;
  }
  
  const currentUserRole = teamMembersData.find(m => m.id === userDetails?.id)?.role ?? null;

  return {
    patientList, consultationList, newPatient, setNewPatient, createPatient, userDetails, consultationLoading, teamMembersData, currentOrg, subscriptionActiveStatus, viewType, fetchCorporateVitalsRecords, currentUserRole
  };
};

